import { useCallback, useMemo, useState } from 'react';

import { UnpaidInvoice } from '../../UnappliedFunds.types';

interface Params {
  invoices: UnpaidInvoice[];
  balance: number;
}

interface SelectedInvoice {
  id: string;
  amount: number;
}

export const useUnpaidInvoicesSelection = ({ invoices, balance }: Params) => {
  const [selectedInvoices, setSelectedInvoices] = useState<SelectedInvoice[]>([]);

  const selectedIds = useMemo(() => selectedInvoices.map(({ id }) => id), [selectedInvoices]);
  const selectedAmount = useMemo(
    () => selectedInvoices.reduce((sum, { amount }) => sum + amount, 0),
    [selectedInvoices]
  );
  const balanceAfterApply = balance - selectedAmount;

  const isAllSelected = useMemo(
    () => invoices.length > 0 && invoices.every(({ id }) => selectedIds.includes(id)),
    [invoices, selectedIds]
  );

  const disabledIds = useMemo(
    () =>
      invoices
        .filter(({ id, amount }) => !selectedIds.includes(id) && amount > balanceAfterApply)
        .map(({ id }) => id),
    [invoices, selectedIds, balanceAfterApply]
  );

  const unselectedPageInvoices = useMemo(
    () => invoices.filter(({ id }) => !selectedIds.includes(id)),
    [invoices, selectedIds]
  );
  const isSelectAllDisabled =
    unselectedPageInvoices.reduce((sum, { amount }) => sum + amount, 0) > balanceAfterApply;

  const toggleInvoice = useCallback(
    (id: string, checked: boolean) => {
      if (!checked) {
        setSelectedInvoices((current) => current.filter((item) => item.id !== id));
        return;
      }
      const invoice = invoices.find((item) => item.id === id);
      if (!invoice || disabledIds.includes(id)) {
        return;
      }
      setSelectedInvoices((current) => [...current, { id, amount: invoice.amount }]);
    },
    [invoices, disabledIds]
  );

  const toggleAll = useCallback(
    (checked: boolean) => {
      if (!checked) {
        setSelectedInvoices((current) => current.filter(({ id }) => !invoices.some((item) => item.id === id)));
        return;
      }
      if (isSelectAllDisabled) {
        return;
      }
      setSelectedInvoices((current) => [
        ...current,
        ...unselectedPageInvoices.map(({ id, amount }) => ({ id, amount })),
      ]);
    },
    [invoices, isSelectAllDisabled, unselectedPageInvoices]
  );

  const clearSelection = useCallback(() => setSelectedInvoices([]), []);

  return {
    selectedIds,
    selectedAmount,
    balanceAfterApply,
    disabledIds,
    isAllSelected,
    isSelectAllDisabled,
    toggleInvoice,
    toggleAll,
    clearSelection,
  };
};
